<script lang="typescript">
    import { onMount } from "svelte";
    import { GeolocateControl, Map } from "maplibre-gl";
    import 'maplibre-gl/dist/maplibre-gl.css';
    import { mainLayerId, styleCuzkBase } from "./maplibreUtils";
    import { gamePolygon, objectives, requiredObjectiveCount, type Objective } from "./objectives";
    import Modal from "./Modal.svelte";

    type GeoJsonPoints<T> = {
        'type': 'geojson',
        'data': {
            'type': 'FeatureCollection',
            'features': {
                'type': 'Feature',
                'geometry': {
                    'type': 'Point',
                    'coordinates': [number, number]
                },
                'properties': T,
                'id': string,
            }[],
        },
    };
    type ObjectivesState = Record<string, boolean>;
    type ObjectivesGeoJson = GeoJsonPoints<{
        name: string;
        solved: boolean;
        id: string;
        special: boolean;
    }>;

    const SOURCE_NAME_MARKERS = "markers_source";
    const SOURCE_NAME_POLYGON = "polygon_source";
    const LAYER_NAME_MARKERS = "markers_layer";
    const LAYER_NAME_LABELS = "markers_layer_labels";
    const LAYER_NAME_POLYGON1 = "polygon_layer1";
    const LAYER_NAME_POLYGON2 = "polygon_layer2";
    const KEY_OBJECTIVES_STATE = "objectives_state";

    let mapContainer: HTMLDivElement;
    let map: Map | null = $state.raw(null);
    let selectedObjective: (Objective & { solved: boolean }) | undefined = $state.raw(undefined);
    let wasAttemptWrong: boolean = $state.raw(false);
    let attemptClearTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
    let solvedCount: number = $state.raw(0);

    function getObjectivesState(): ObjectivesState {
        const stored = localStorage.getItem(KEY_OBJECTIVES_STATE);
        if (!stored || stored.length === 0) {
            return {};
        }
        return JSON.parse(stored) as ObjectivesState;
    }

    function setObjectivesState(state: ObjectivesState): void {
        localStorage.setItem(KEY_OBJECTIVES_STATE, JSON.stringify(state));
    }

    function updateSolvedCount(): void {
        const state = getObjectivesState();
        let count = 0;
        for(const key in state) {
            if (state[key]) {
                count++;
            }
        }
        solvedCount = count;
    }

    function getMarkersGeoJson(): ObjectivesGeoJson {
        const geojson: ObjectivesGeoJson = {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: [],
            },
        };
        const state = getObjectivesState();
        for (const o of objectives) {
            const solved = state[o.id] ?? false;
            geojson.data.features.push({
                type: "Feature",
                id: o.id,
                geometry: {
                    type: "Point",
                    coordinates: [o.lng, o.lat],
                },
                properties: {
                    name: o.name,
                    solved,
                    id: o.id,
                    special: !!o.special,
                },
            });
        }
        return geojson;
    }

    function onMapChange() {
        if (!map) {
            return;
        }
        map.setStyle(styleCuzkBase);
        if (map.getLayer(LAYER_NAME_MARKERS)) {
            map.removeLayer(LAYER_NAME_MARKERS);
        }
        if (map.getLayer(LAYER_NAME_LABELS)) {
            map.removeLayer(LAYER_NAME_LABELS);
        }
        if (map.getLayer(LAYER_NAME_POLYGON1)) {
            map.removeLayer(LAYER_NAME_POLYGON1);
        }
        if (map.getLayer(LAYER_NAME_POLYGON2)) {
            map.removeLayer(LAYER_NAME_POLYGON2);
        }
        if (map.getSource(SOURCE_NAME_MARKERS)) {
            map.removeSource(SOURCE_NAME_MARKERS);
        }
        if (map.getSource(SOURCE_NAME_POLYGON)) {
            map.removeSource(SOURCE_NAME_POLYGON);
        }

        map.addSource(SOURCE_NAME_MARKERS, {type: 'geojson', data: getMarkersGeoJson().data});
        map.addSource(SOURCE_NAME_POLYGON, {type: 'geojson', data: gamePolygon.data});
        map.addLayer({
            'id': LAYER_NAME_POLYGON1,
            'type': 'line',
            'source': SOURCE_NAME_POLYGON,
            'paint': {
                "line-color": "#000",
                "line-width": 9,
            },
        });
        map.addLayer({
            'id': LAYER_NAME_POLYGON2,
            'type': 'line',
            'source': SOURCE_NAME_POLYGON,
            'paint': {
                "line-color": "#fff",
                "line-width": 3,
            },
        });
        map.addLayer({
            'id': LAYER_NAME_MARKERS,
            'type': 'circle',
            'source': SOURCE_NAME_MARKERS,
            'paint': {
                "circle-color": [
                    'case',
                    ['==', ['get', 'id'], 'objekt'],
                    "#24f",
                    [
                        'case',
                        ['boolean', ['get', 'solved'], false],
                        "#4f2",
                        "#f41",
                    ],
                ],
                "circle-opacity": 1.0,
                "circle-radius": 10,
                "circle-stroke-width": 2,
                "circle-stroke-color": "#000000",
                "circle-stroke-opacity": 1.0,
            },
        });
        map.addLayer({
            'id': LAYER_NAME_LABELS,
            'type': 'symbol',
            'source': SOURCE_NAME_MARKERS,
            'layout': {
                'text-size': 30,
                'text-field': ['get', 'name'],
                'text-offset': [0, 1],
                'symbol-sort-key': ['get', 'typePriority']
            },
            'paint': {
                'text-color': "#fff",
                'text-halo-width': 3,
                'text-halo-color': "#000",
            },
        });
    }

    onMount(() => {
        const m = new Map({
            container: mapContainer,
            zoom: 11,
            center: [15.3486247, 49.5571300],
            style: styleCuzkBase,
            pitchWithRotate: false,
            dragRotate: false,
        });
        // Add geolocate control to the map.
        m.addControl(
            new GeolocateControl({
                positionOptions: {
                    enableHighAccuracy: true
                },
                trackUserLocation: true
            })
        );
        if (m.isStyleLoaded()) {
            mapLoaded(m);
        } else {
            m.once("style.load", () => {
                mapLoaded(m);
            });
        }
        updateSolvedCount();
    });

    function mapLoaded(m: Map) {
        map = m;
        map.on('click', LAYER_NAME_MARKERS, (e) => {
            const coordinates = (e.features[0].geometry as any).coordinates.slice();
            map.easeTo({
                center: coordinates,
            });
            const id = e.features[0].properties.id as string;
            const obj = objectives.find(x => x.id === id);
            const state = getObjectivesState();
            selectedObjective = {
                ...obj,
                solved: state[id],
            };
            wasAttemptWrong = false;
            if (attemptClearTimeout !== undefined) {
                clearTimeout(attemptClearTimeout);
                attemptClearTimeout = undefined;
            }
        });
        onMapChange();
    }

    function normalizeText(str: string): string {
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();
    }

    let passwordTextInput: HTMLInputElement;

    function solveAttempt(): void {
        if (attemptClearTimeout !== undefined) {
            clearTimeout(attemptClearTimeout);
            attemptClearTimeout = undefined;
        }
        if (!selectedObjective) {
            return;
        }
        const text = normalizeText(passwordTextInput.value);
        let solved = false;
        for(const sol of selectedObjective.solutions) {
            if (normalizeText(sol) === text) {
                solved = true;
                break;
            }
        }
        if (solved) {
            const state = getObjectivesState();
            state[selectedObjective.id] = true;
            setObjectivesState(state);
            selectedObjective = {
                ...selectedObjective,
                solved: true,
            };
            onMapChange();
            updateSolvedCount();
        } else {
            wasAttemptWrong = true;
            attemptClearTimeout = setTimeout(() => {
                wasAttemptWrong = false;
            }, 1500);
        }
    }

    function textInputKeyDown(event: KeyboardEvent): void {
        if(event.key === 'Enter') {
            solveAttempt();        
        }
    }

    function ukolyPlural(count: number): string {
        if (count === 1) {
            return "úkol";
        }
        if (count >= 2 && count <= 4) {
            return "úkoly";
        }
        return "úkolů";
    }
    function zPlural(count: number): string {
        return (count >= 2 && count <= 4) ? "ze" : "z";
    }
</script>

<style>
    #map { height: 100%; }

    .fullscreen {
        height: calc(100vh - 1px);
    }
    .modal {
        background-color: #eee;
        max-width: 20em;
        margin: auto;
        padding: 1em;
    }
    .description {
        margin: 0.5em 0;
    }
    .heading {
        margin: 0;
    }
    .inputs {
        margin: 0.5em 0;
    }
    .count {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        bottom: 0.5em;
        padding: 1em;
        border: 0.1em solid;
        border-radius: 1em;
        border-color: #000;
        width: calc(100vw - 2em);
        max-width: 40em;
        box-sizing: border-box;
    }
</style>

<div class="fullscreen">
    <div id="map" bind:this={mapContainer}></div>
    <div class="count" style="background-color: {solvedCount >= requiredObjectiveCount ? '#dfd' : '#eee'}">
        {#if solvedCount >= requiredObjectiveCount}
            Splnili jste {solvedCount} {ukolyPlural(solvedCount)} {zPlural(requiredObjectiveCount)} {requiredObjectiveCount} potřebných.
            Můžete opustit fotící zónu!
        {:else}
            Splnili jste {solvedCount} {ukolyPlural(solvedCount)}.
            Pro výstup z fotící zóny potřebujete splnit aspoň {requiredObjectiveCount} {ukolyPlural(requiredObjectiveCount)}.
        {/if}
    </div>
    {#if selectedObjective}
        <Modal on:close={selectedObjective = undefined}>
            <div class="modal">
                {#if !selectedObjective.special}
                    <h2 class="heading">Úkol {selectedObjective.name}</h2>
                    <div class="description">
                        {@html selectedObjective.description}
                    </div>
                    <div class="inputs">
                        {#if selectedObjective.solved}
                            ✅ Hotovo! Heslo bylo {selectedObjective.solutions[0]}.
                        {:else}
                            <input type="text" bind:this={passwordTextInput} on:keydown={textInputKeyDown}>
                            <button on:click={solveAttempt}>Zadat</button>
                            {#if wasAttemptWrong}
                                <span style="color:darkred">Špatně.</span>
                            {/if}
                        {/if}
                    </div>
                {:else}
                    <h2 class="heading">{selectedObjective.name}</h2>
                    <div class="description">
                        {@html selectedObjective.description}
                    </div>
                {/if}
            </div>
        </Modal>
    {/if}
</div>

